import { useCallback, useState } from "react";
import { useCreateTransactionMutation } from "@/datasource/http/point-transactions/mutation";
import { useCalculationQuery } from "@/datasource/http/point-transactions/query";
import type { TransactionCalculation } from "@/datasource/http/point-transactions/services";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useIdempotencyKey } from "@/hooks/use-idempotency-key";
import type { PointTransaction } from "@/models/point-transaction";

export type WizardStep = "details" | "confirmation" | "success";

export interface WizardCalculationParams {
  loyaltyAccountId: number;
  purchaseValueCents: number;
  transactionType: string;
  points?: number;
}

export interface UseCreateTransactionWizardReturn {
  // Step management
  currentStep: WizardStep;
  goToNext: () => void;
  goToPrevious: () => void;
  reset: () => void;
  setStep: (step: WizardStep) => void;

  // Calculation
  calculation: TransactionCalculation | undefined;
  isCalculating: boolean;
  calculationError: Error | null;
  setCalculationParams: (params: WizardCalculationParams) => void;

  // Mutation
  createMutation: ReturnType<typeof useCreateTransactionMutation>;
  idempotencyKey: string;
  regenerateIdempotencyKey: () => void;

  // Success state
  createdTransaction: PointTransaction | null;
  setCreatedTransaction: (transaction: PointTransaction | null) => void;
}

const STEP_ORDER: WizardStep[] = ["details", "confirmation", "success"];

export function useCreateTransactionWizard(): UseCreateTransactionWizardReturn {
  const [currentStep, setCurrentStep] = useState<WizardStep>("details");
  const [createdTransaction, setCreatedTransaction] = useState<PointTransaction | null>(null);

  // Calculation params with debouncing
  const [calculationParams, setCalculationParams] = useState<WizardCalculationParams>({
    loyaltyAccountId: 0,
    purchaseValueCents: 0,
    transactionType: "purchase",
    points: undefined,
  });

  // Debounce purchase value to avoid excessive API calls
  const [debouncedPurchaseValue] = useDebouncedValue(calculationParams.purchaseValueCents, 500);
  const [debouncedDirectPoints] = useDebouncedValue(calculationParams.points, 500);

  // Calculation query
  const { data: calculation, isFetching: isCalculating, error: calculationError } = useCalculationQuery({
    loyalty_account_id: calculationParams.loyaltyAccountId,
    purchase_value_cents: debouncedPurchaseValue,
    transaction_type: calculationParams.transactionType,
    points: debouncedDirectPoints,
  });

  // Mutation
  const createMutation = useCreateTransactionMutation();

  // Idempotency key
  const { key: idempotencyKey, regenerate: regenerateIdempotencyKey } = useIdempotencyKey();

  // Step navigation
  const goToNext = useCallback(function goToNextStep() {
    setCurrentStep((current) => {
      const currentIndex = STEP_ORDER.indexOf(current);
      if (currentIndex < STEP_ORDER.length - 1) {
        return STEP_ORDER[currentIndex + 1];
      }
      return current;
    });
  }, []);

  const goToPrevious = useCallback(function goToPreviousStep() {
    setCurrentStep((current) => {
      const currentIndex = STEP_ORDER.indexOf(current);
      if (currentIndex > 0) {
        return STEP_ORDER[currentIndex - 1];
      }
      return current;
    });
  }, []);

  const reset = useCallback(
    function resetWizard() {
      setCurrentStep("details");
      setCreatedTransaction(null);
      setCalculationParams({
        loyaltyAccountId: 0,
        purchaseValueCents: 0,
        transactionType: "purchase",
        points: undefined,
      });
      createMutation.reset();
      regenerateIdempotencyKey();
    },
    [createMutation, regenerateIdempotencyKey],
  );

  const setStep = useCallback(function setWizardStep(step: WizardStep) {
    setCurrentStep(step);
  }, []);

  const updateCalculationParams = useCallback(function updateParams(
    params: WizardCalculationParams,
  ) {
    setCalculationParams(params);
  }, []);

  return {
    currentStep,
    goToNext,
    goToPrevious,
    reset,
    setStep,
    calculation: calculation?.data.calculation,
    isCalculating,
    calculationError: calculationError ?? null,
    setCalculationParams: updateCalculationParams,
    createMutation,
    idempotencyKey,
    regenerateIdempotencyKey,
    createdTransaction,
    setCreatedTransaction,
  };
}
